import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function InternalNotes({ phone, userId }) {
  const [notes, setNotes] = useState([]);
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [refresh, setRefresh] = useState(0);
  const saveLock = useRef(false);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const endpoint = userId ? `/supervisor/users/${userId}/internal-notes` : "/internal-notes";
  useEffect(() => {
    let active = true, busy = false;
    setLoading(true); setNotes([]); setError("");
    const load = async () => {
      if (busy) return;
      busy = true;
      try {
        const { data } = await api.get(endpoint, { params: { phone }, timeout: 15000 });
        if (!Array.isArray(data)) throw new Error("Invalid notes response");
        if (active) { setNotes(data); setError(""); }
      } catch { if (active) setError("Não foi possível carregar as notas. Tentando novamente..."); }
      finally { busy = false; if (active) setLoading(false); }
    };
    load(); const timer = setInterval(load, 5000);
    return () => { active = false; clearInterval(timer); };
  }, [endpoint, phone, refresh]);
  const save = async event => {
    event.preventDefault();
    if (!userId || saveLock.current || !draft.trim()) return;
    saveLock.current = true; setSaving(true);
    try {
      await api.post(endpoint, { phone, text: draft.trim() }, { timeout: 15000 });
      toast.success("Nota interna salva");
      if (mounted.current) { setDraft(""); setRefresh(n => n + 1); }
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Não foi possível confirmar a gravação. Confira as notas antes de tentar novamente.");
    } finally { saveLock.current = false; if (mounted.current) setSaving(false); }
  };
  return (
    <section aria-label="Notas internas" className="flex flex-col flex-1 min-h-0 gap-3 p-3">
      <p className="text-xs text-pink-300">Orientações internas entre supervisão e consultora. Estas notas não são enviadas ao WhatsApp do cliente.</p>
      <div className="flex-1 overflow-y-auto min-h-0 space-y-3">
        {loading && <p role="status" className="text-sm text-slate-400">Carregando notas...</p>}
        {error && <p role="alert" className="text-sm text-rose-400">{error}</p>}
        {!loading && !error && notes.length === 0 && <p className="text-sm text-slate-400">Nenhuma nota nesta conversa.</p>}
        {notes.map(note => (
          <article key={note.id} className="rounded-lg border border-pink-500/30 bg-pink-500/10 p-3">
            <div className="text-xs text-pink-300 mb-1">{note.created_by_name || "Supervisão"}
              {note.created_at && <span className="ml-2 text-slate-400">{new Date(note.created_at).toLocaleString("pt-BR")}</span>}
            </div>
            <p className="text-sm text-slate-100 whitespace-pre-wrap break-words">{note.text}</p>
          </article>
        ))}
      </div>
      {userId && <form onSubmit={save} className="space-y-2 border-t border-slate-800 pt-3">
        <textarea aria-label="Nova nota interna" placeholder="Escreva uma orientação para a consultora..." rows={3} maxLength={5000}
          value={draft} onChange={e => setDraft(e.target.value)} disabled={saving}
          className="w-full resize-none rounded border border-pink-500/40 bg-slate-900 px-3 py-2 text-sm" />
        <Button type="submit" disabled={saving || !draft.trim()}>{saving ? "Salvando..." : "Salvar nota interna"}</Button>
      </form>}
    </section>
  );
}
